import { useEffect, useRef, useState } from "react";
import { Drawer } from "vaul";

import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bell, Check, CheckCheck, Heart, MessageCircle, UserPlus, Tag, X, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useIdle } from "@/hooks/useIdle";
import {
  listNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  deleteNotification,
  type NotificationRow,
} from "@/lib/notifications.functions";
import { cn } from "@/lib/utils";

const ICONS: Record<string, typeof Heart> = {
  like: Heart,
  comment: MessageCircle,
  follow: UserPlus,
  wishlist_for_sale: Tag,
  album_comment: MessageCircle,
  album_comment_reply: MessageCircle,
  comment_reply: MessageCircle,
  community_like: Heart,
  community_comment: MessageCircle,
  community_comment_reply: MessageCircle,
  group_like: Heart,
  group_comment: MessageCircle,
  group_comment_reply: MessageCircle,
  message: MessageCircle,
};

const ICON_COLORS: Record<string, string> = {
  like: "text-rose-500",
  comment: "text-sky-500",
  follow: "text-emerald-500",
  wishlist_for_sale: "text-amber-500",
  album_comment: "text-sky-500",
  album_comment_reply: "text-sky-500",
  comment_reply: "text-sky-500",
  community_like: "text-rose-500",
  community_comment: "text-sky-500",
  community_comment_reply: "text-sky-500",
  group_like: "text-rose-500",
  group_comment: "text-sky-500",
  group_comment_reply: "text-sky-500",
  message: "text-violet-500",
};

/** Bottom sheet em mobile e tablet (< lg), popover no desktop. */
function useIsCompact() {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setCompact(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return compact;
}

/** Avatar do autor da notificação com fallback em iniciais. */
function ActorAvatar({
  actor,
}: {
  actor: NotificationRow["actor"];
}) {
  const [failed, setFailed] = useState(false);
  const name = actor?.display_name || actor?.username || "";
  const initials = name.trim().slice(0, 2).toUpperCase();
  const src = actor?.avatar_url && !failed ? actor.avatar_url : null;

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        loading="lazy"
        referrerPolicy="no-referrer"
        
        onError={() => setFailed(true)}
        className="h-10 w-10 rounded-full bg-surface object-cover"
      />
    );
  }

  return (
    <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-xs font-bold uppercase text-primary">
      {initials || <Bell className="h-4 w-4 text-muted-foreground" />}
    </div>
  );
}

export function NotificationBell() {
  const { isAuthenticated, profile } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const isCompact = useIsCompact();
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const listFn = useServerFn(listNotifications);
  const countFn = useServerFn(getUnreadNotificationCount);
  const readFn = useServerFn(markNotificationRead);
  const allFn = useServerFn(markAllNotificationsRead);
  const clearFn = useServerFn(clearAllNotifications);
  const delFn = useServerFn(deleteNotification);

  const idle = useIdle();
  const countQ = useQuery({
    queryKey: ["notif-unread"],
    queryFn: () => countFn(),
    // Deferred until idle: the badge shouldn't delay the page's own data.
    enabled: isAuthenticated && idle,
    staleTime: 3 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: true,
  });


  const listQ = useQuery({
    queryKey: ["notif-list"],
    queryFn: () => listFn(),
    enabled: isAuthenticated && open,
    staleTime: 10_000,
  });

  // Live update via realtime
  useEffect(() => {
    if (!isAuthenticated || !profile?.id) return;
    const channel = supabase.channel(`notif-${profile.id}-${Math.random().toString(36).slice(2)}`);
    channel.on(
      "postgres_changes" as never,
      { event: "*", schema: "public", table: "notifications", filter: `recipient_id=eq.${profile.id}` },
      () => {
        qc.invalidateQueries({ queryKey: ["notif-unread"] });
        qc.invalidateQueries({ queryKey: ["notif-list"] });
      },
    );
    channel.subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, profile?.id, qc]);

  // Click outside to close
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open && !isCompact) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open, isCompact]);

  const markRead = useMutation({
    mutationFn: (id: string) => readFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
  const markAll = useMutation({
    mutationFn: () => allFn(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
  const clearAll = useMutation({
    mutationFn: () => clearFn(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  if (!isAuthenticated) return null;

  const unread = countQ.data?.count ?? 0;
  const items = listQ.data ?? [];

  const handleItemClick = (n: NotificationRow) => {
    if (!n.read_at) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) {
      // Use full navigation for links with hash / query so the target page can scroll into view
      if (n.link.includes("#") || n.link.includes("?")) {
        window.location.assign(n.link);
      } else {
        navigate({ to: n.link as never });
      }
    }
  };

  const panelBody = (
    <>
      <div className="flex items-center justify-between border-b border-[color:var(--hairline)] px-4 py-3">

            <div>
              <div className="text-base font-semibold lg:text-sm">Notificações</div>
              {unread > 0 && (
                <div className="text-[11px] text-muted-foreground">{unread} não lidas</div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                  title="Marcar todas como lidas"
                  className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-surface hover:text-foreground"
                >
                  <CheckCheck className="h-4 w-4" />
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={() => clearAll.mutate()}
                  disabled={clearAll.isPending}
                  title="Limpar todas"
                  className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-surface hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fechar"
                className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-surface hover:text-foreground lg:hidden"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] lg:max-h-[60vh]">

            {listQ.isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-14 animate-pulse rounded-xl bg-surface/60" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/40" />
                <div className="text-sm font-medium">Tudo em dia</div>
                <p className="text-xs text-muted-foreground">
                  Você verá aqui curtidas, comentários, seguidores e álbuns da
                  sua wishlist quando entrarem à venda.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[color:var(--hairline)]">
                {items.map((n) => {
                  const Icon = ICONS[n.type] ?? Bell;
                  return (
                    <li
                      key={n.id}
                      className={cn(
                        "group relative flex items-start gap-3 px-4 py-3 transition-colors hover:bg-surface/60",
                        !n.read_at && "bg-primary/[0.04]",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleItemClick(n)}
                        className="flex flex-1 items-start gap-3 text-left"
                      >
                        <div className="relative shrink-0">
                          <ActorAvatar actor={n.actor} />

                          <span
                            className={cn(
                              "absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-background ring-2 ring-background",
                              ICON_COLORS[n.type],
                            )}
                          >
                            <Icon className="h-3 w-3" fill="currentColor" strokeWidth={0} />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm leading-snug lg:text-[13px]">
                            <span className="font-semibold">{n.title}</span>
                            {n.body && !n.album && (
                              <span className="text-muted-foreground"> {n.body}</span>
                            )}
                          </div>
                          {n.album && (
                            <div className="mt-0.5 truncate text-[12px] text-muted-foreground">
                              {n.album.title}
                              {n.album.artist ? ` — ${n.album.artist}` : ""}
                            </div>
                          )}
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {formatDistanceToNow(new Date(n.created_at), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </div>
                        </div>
                        {n.album?.cover_url && (
                          <img
                            src={n.album.cover_url}
                            alt={n.album.title ?? ""}
                            loading="lazy"
                            className="h-12 w-12 shrink-0 rounded-md object-cover ring-1 ring-[color:var(--hairline)]"
                          />
                        )}
                        {!n.read_at && (
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                        )}

                      </button>
                      <div className="flex shrink-0 flex-col gap-0.5 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
                        {!n.read_at && (
                          <button
                            type="button"
                            onClick={() => markRead.mutate(n.id)}
                            title="Marcar como lida"
                            className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => del.mutate(n.id)}
                          title="Remover"
                          className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
      </div>
    </>
  );

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notificações"
        className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-surface hover:text-foreground"
      >
        <Bell className="h-[18px] w-[18px] lg:h-4 lg:w-4" />
        {unread > 0 && (
          <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-background">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {isCompact ? (
        <Drawer.Root open={open} onOpenChange={setOpen} shouldScaleBackground={false} dismissible>
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[88dvh] flex-col rounded-t-3xl border border-border bg-popover shadow-2xl outline-none">
              <div
                data-vaul-drag-handle
                className="flex w-full shrink-0 cursor-grab items-center justify-center py-2.5 active:cursor-grabbing"
              >
                <div className="h-1.5 w-12 rounded-full bg-muted-foreground/40" />
              </div>
              <Drawer.Title className="sr-only">Notificações</Drawer.Title>
              {panelBody}
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      ) : (
        open && (
          <div className="absolute right-0 top-[calc(100%+8px)] z-50 max-h-[80vh] w-[440px] overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-popover/95 shadow-[var(--shadow-soft)] backdrop-blur-xl">
            {panelBody}
          </div>
        )
      )}
    </div>
  );
}

